import { PromotionalType } from '../__autogenerated__/types/PromotionalType';
import { CreditCard } from '../CreditCard';
import { CreditCardDataFiltered } from '../CreditCardData';

// Configuration for how each promotional type should be displayed and interacted with
export type PromotionalTypeInputConfig =
  | {
      type: 'boolean';
      label: string;
      description?: string;
    }
  | {
      type: 'enum';
      label: string;
      description?: string;
      options: ReadonlyArray<{
        readonly value: string;
        readonly label: string;
        readonly description?: string;
      }>;
    };

export type PromotionalTypesConfig = Partial<
  Record<PromotionalType, PromotionalTypeInputConfig>
>;

// Preferred Rewards tier options
export const PREFERRED_REWARDS_OPTIONS = [
  {
    value: 'none',
    label: 'None',
    description: 'Not enrolled in Preferred Rewards',
  },
  {
    value: 'gold',
    label: 'Preferred Rewards Gold',
    description: '$20,000 minimum (25% bonus)',
  },
  {
    value: 'platinum',
    label: 'Preferred Rewards Platinum',
    description: '$50,000 minimum (50% bonus)',
  },
  {
    value: 'platinum_honors',
    label: 'Preferred Rewards Platinum Honors',
    description: '$100,000 minimum (75% bonus)',
  },
  {
    value: 'diamond_honors',
    label: 'Preferred Rewards Diamond Honors',
    description: '$1,000,000 minimum (75% bonus)',
  },
] as const;

// US Bank Smartly tier options
export const US_BANK_SMARTLY_OPTIONS = [
  {
    value: 'none',
    label: 'None',
    description: 'Below $10,000 qualifying balance',
  },
  {
    value: 'tier1',
    label: 'Tier 1',
    description: '$10,000-$49,999 (2.5x total)',
  },
  {
    value: 'tier2',
    label: 'Tier 2',
    description: '$50,000-$99,999 (3x total)',
  },
  {
    value: 'tier3',
    label: 'Tier 3',
    description: '$100,000+ (4x total)',
  },
] as const;

// Bilt spend ratio tier options
export const BILT_SPEND_RATIO_OPTIONS = [
  {
    value: 'none',
    label: 'None',
    description: 'No housing points earned',
  },
  {
    value: '25',
    label: '25%',
    description: 'Earn 0.5X on housing',
  },
  {
    value: '50',
    label: '50%',
    description: 'Earn 0.75X on housing',
  },
  {
    value: '75',
    label: '75%',
    description: 'Earn 1X on housing',
  },
  {
    value: '100',
    label: '100%',
    description: 'Earn 1.25X on housing',
  },
] as const;

// Map tier-specific promotional types to their display names
export const PREFERRED_REWARDS_TIER_NAMES: Partial<
  Record<PromotionalType, string>
> = {
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_GOLD]:
    'Preferred Rewards Gold',
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM]:
    'Preferred Rewards Platinum',
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM_HONORS]:
    'Preferred Rewards Platinum Honors',
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_DIAMOND_HONORS]:
    'Preferred Rewards Diamond Honors',
} as const;

// Map Bilt spend ratio promotional types to their display names
export const BILT_SPEND_RATIO_TIER_NAMES: Partial<
  Record<PromotionalType, string>
> = {
  [PromotionalType.BILT_SPEND_RATIO_25]: '25%',
  [PromotionalType.BILT_SPEND_RATIO_50]: '50%',
  [PromotionalType.BILT_SPEND_RATIO_75]: '75%',
  [PromotionalType.BILT_SPEND_RATIO_100]: '100%',
} as const;

export const promotionalTypesConfig: PromotionalTypesConfig = {
  // Tier-specific promotional types
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_GOLD]: {
    type: 'boolean' as const,
    label: 'Preferred Rewards Gold',
    description: '$20,000 minimum (25% bonus)',
  },
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM]: {
    type: 'boolean' as const,
    label: 'Preferred Rewards Platinum',
    description: '$50,000 minimum (50% bonus)',
  },
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM_HONORS]: {
    type: 'boolean' as const,
    label: 'Preferred Rewards Platinum Honors',
    description: '$100,000 minimum (75% bonus)',
  },
  [PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_DIAMOND_HONORS]: {
    type: 'boolean' as const,
    label: 'Preferred Rewards Diamond Honors',
    description: '$1,000,000 minimum (75% bonus)',
  },
  // Tier-specific promotional types for US Bank Smartly
  [PromotionalType.US_BANK_SMARTLY_TIER_1]: {
    type: 'boolean' as const,
    label: '25% Smartly Earning Bonus',
    description: '$10,000-$49,999 qualifying balance (earn 2.5% total)',
  },
  [PromotionalType.US_BANK_SMARTLY_TIER_2]: {
    type: 'boolean' as const,
    label: '50% Smartly Earning Bonus',
    description: '$50,000-$99,999 qualifying balance (earn 3% total)',
  },
  [PromotionalType.US_BANK_SMARTLY_TIER_3]: {
    type: 'boolean' as const,
    label: '100% Smartly Earning Bonus',
    description: '$100,000+ qualifying balance (earn 4% total)',
  },
  [PromotionalType.BLOOMINGDALES_TOP_OF_LIST_TIER]: {
    type: 'boolean',
    label: "Bloomingdale's Top of the List Tier",
    description:
      "Check if you have Top of the List status in the Bloomingdale's loyalty program",
  },
  [PromotionalType.BLOOMINGDALES_LOYALIST_TIER]: {
    type: 'boolean',
    label: "Bloomingdale's Loyallist Tier",
    description:
      "Check if you have Loyallist status in the Bloomingdale's loyalty program",
  },
  [PromotionalType.REQUIRE_BANK_OF_AMERICA_SMALL_BUSINESS_ACCOUNT]: {
    type: 'boolean',
    label: 'Bank of America Small Business Account',
    description:
      'Check if you have an eligible Bank of America small business account',
  },
  [PromotionalType.PRIME_MEMBERSHIP]: {
    type: 'boolean',
    label: 'Amazon Prime Membership',
    description: 'Check if you have an active Amazon Prime membership',
  },
  [PromotionalType.UPROMISE_COLLEGE_SAVINGS_PLAN]: {
    type: 'boolean',
    label: 'Upromise College Savings Plan',
    description:
      'Check if you have an Upromise College Savings Plan account linked',
  },
  [PromotionalType.BILT_CONNECT_TO_LYFT]: {
    type: 'boolean',
    label: 'Bilt Connect to Lyft',
    description: 'Check if you have connected your Bilt account to Lyft',
  },
  [PromotionalType.BILT_RENT_DAY]: {
    type: 'boolean',
    label: 'Bilt Rent Day',
    description:
      'Check if you use Bilt Rent Day (first of the month) for rent payments',
  },
  // Bilt spend ratio tiers - earn rate on housing based on everyday spend
  [PromotionalType.BILT_SPEND_RATIO_25]: {
    type: 'boolean' as const,
    label: '25%',
    description: 'Earn 0.5X on housing',
  },
  [PromotionalType.BILT_SPEND_RATIO_50]: {
    type: 'boolean' as const,
    label: '50%',
    description: 'Earn 0.75X on housing',
  },
  [PromotionalType.BILT_SPEND_RATIO_75]: {
    type: 'boolean' as const,
    label: '75%',
    description: 'Earn 1X on housing',
  },
  [PromotionalType.BILT_SPEND_RATIO_100]: {
    type: 'boolean' as const,
    label: '100%',
    description: 'Earn 1.25X on housing',
  },
  // PenFed Honors Advantage tier
  [PromotionalType.PENFED_HONORS_ADVANTAGE]: {
    type: 'boolean' as const,
    label: 'PenFed Honors Advantage',
    description:
      'Active military, reserves, veteran, or have a qualifying PenFed checking account (earns 4X travel, 3X dining instead of 3X/2X)',
  },
};

/**
 * Groups promotional types by "family" - e.g., all Preferred Rewards tiers together
 */
export function groupPromotionalTypesByFamily(
  types: PromotionalType[]
): Map<string, PromotionalType[]> {
  const groups = new Map<string, PromotionalType[]>();

  // Define promotional type families
  const preferredRewardsFamily: PromotionalType[] = [
    PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_GOLD,
    PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM,
    PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_PLATINUM_HONORS,
    PromotionalType.BANK_OF_AMERICA_PREFERRED_REWARDS_DIAMOND_HONORS,
  ];

  // Define US Bank Smartly tier family
  const usBankSmartlyFamily: PromotionalType[] = [
    PromotionalType.US_BANK_SMARTLY_TIER_1,
    PromotionalType.US_BANK_SMARTLY_TIER_2,
    PromotionalType.US_BANK_SMARTLY_TIER_3,
  ];

  // Define Bilt spend ratio tier family
  const biltSpendRatioFamily: PromotionalType[] = [
    PromotionalType.BILT_SPEND_RATIO_25,
    PromotionalType.BILT_SPEND_RATIO_50,
    PromotionalType.BILT_SPEND_RATIO_75,
    PromotionalType.BILT_SPEND_RATIO_100,
  ];

  for (const type of types) {
    // Check if this is a Preferred Rewards tier
    if (preferredRewardsFamily.includes(type)) {
      const familyKey = 'bank_of_america_preferred_rewards';
      if (!groups.has(familyKey)) {
        groups.set(familyKey, []);
      }
      const familyGroup = groups.get(familyKey);
      if (familyGroup) {
        familyGroup.push(type);
      }
    } else if (usBankSmartlyFamily.includes(type)) {
      // Check if this is a US Bank Smartly tier
      const familyKey = 'us_bank_smartly';
      if (!groups.has(familyKey)) {
        groups.set(familyKey, []);
      }
      const familyGroup = groups.get(familyKey);
      if (familyGroup) {
        familyGroup.push(type);
      }
    } else if (biltSpendRatioFamily.includes(type)) {
      // Check if this is a Bilt spend ratio tier
      const familyKey = 'bilt_spend_ratio';
      if (!groups.has(familyKey)) {
        groups.set(familyKey, []);
      }
      const familyGroup = groups.get(familyKey);
      if (familyGroup) {
        familyGroup.push(type);
      }
    } else {
      // Each other type is its own family
      groups.set(type, [type]);
    }
  }

  return groups;
}

/**
 * Extracts all promotional types that are applicable to the given cards.
 * This looks through all earn rates and their promotions to find which
 * promotional types are used.
 */
export function getApplicablePromotionalTypes(
  cards: CreditCard[]
): PromotionalType[] {
  // Early return if no cards provided
  if (!cards || cards.length === 0) {
    return [];
  }

  const promotionalTypes = new Set<PromotionalType>();

  // Filter to only valid cards that exist in CreditCardDataFiltered
  const validCards = cards.filter((card) => {
    return (
      card != null &&
      card in CreditCardDataFiltered &&
      CreditCardDataFiltered[card] != null
    );
  });

  // If no valid cards, return empty array
  if (validCards.length === 0) {
    return [];
  }

  for (const card of validCards) {
    const cardData = CreditCardDataFiltered[card];

    // Double-check card data exists and has earn rates
    if (
      !cardData?.rewards_earn_rates ||
      !Array.isArray(cardData.rewards_earn_rates)
    ) {
      continue;
    }

    // Check each earn rate for promotions
    for (const earnRate of cardData.rewards_earn_rates) {
      if (earnRate?.promotions && Array.isArray(earnRate.promotions)) {
        for (const promotion of earnRate.promotions) {
          // First check if promotional_type is explicitly set (not null or undefined)
          if (promotion?.promotional_type != null) {
            // Convert to string for consistent comparison
            // This handles both enum references and string values
            const promoTypeStr = String(promotion.promotional_type);

            // Get all valid enum values as strings
            const validValues = Object.values(PromotionalType) as string[];

            // Check if it's a valid PromotionalType enum value
            if (validValues.includes(promoTypeStr)) {
              promotionalTypes.add(promoTypeStr as PromotionalType);
            }
          }
        }
      }
    }
  }

  return Array.from(promotionalTypes);
}
